// Edge Crossing Utilities
// Detects where edges cross and builds SVG paths with bridge arcs at crossing points.
export { pathToSegments } from "./svgPathParser";
const ENDPOINT_THRESHOLD = 0.01;
/** Minimum overlap length (px) for collinear segments to be considered crossing */ const MIN_COLLINEAR_OVERLAP = 20;
export function segmentIntersection(a, b) {
    const dx1 = a.x2 - a.x1;
    const dy1 = a.y2 - a.y1;
    const dx2 = b.x2 - b.x1;
    const dy2 = b.y2 - b.y1;
    const denom = dx1 * dy2 - dy1 * dx2;
    if (Math.abs(denom) < 1e-10) {
        return collinearOppositeOverlap(a, b, {
            dx1,
            dy1,
            dx2,
            dy2
        });
    }
    const dx3 = b.x1 - a.x1;
    const dy3 = b.y1 - a.y1;
    const t = (dx3 * dy2 - dy3 * dx2) / denom;
    const u = (dx3 * dy1 - dy3 * dx1) / denom;
    if (t < ENDPOINT_THRESHOLD || t > 1 - ENDPOINT_THRESHOLD || u < ENDPOINT_THRESHOLD || u > 1 - ENDPOINT_THRESHOLD) {
        return null;
    }
    return {
        x: a.x1 + t * dx1,
        y: a.y1 + t * dy1,
        t,
        u
    };
}
function collinearOppositeOverlap(a, b, deltas) {
    const { dx1, dy1, dx2, dy2 } = deltas;
    const dot = dx1 * dx2 + dy1 * dy2;
    if (dot >= 0) return null;
    const lenA = Math.sqrt(dx1 * dx1 + dy1 * dy1);
    const lenB = Math.sqrt(dx2 * dx2 + dy2 * dy2);
    if (lenA < 1 || lenB < 1) return null;
    const perpDist = Math.abs((b.x1 - a.x1) * (-dy1 / lenA) + (b.y1 - a.y1) * (dx1 / lenA));
    if (perpDist > 2) return null;
    const ux = dx1 / lenA;
    const uy = dy1 / lenA;
    const projA1 = 0;
    const projA2 = lenA;
    const projB1 = (b.x1 - a.x1) * ux + (b.y1 - a.y1) * uy;
    const projB2 = (b.x2 - a.x1) * ux + (b.y2 - a.y1) * uy;
    const overlapStart = Math.max(Math.min(projA1, projA2), Math.min(projB1, projB2));
    const overlapEnd = Math.min(Math.max(projA1, projA2), Math.max(projB1, projB2));
    if (overlapEnd - overlapStart < MIN_COLLINEAR_OVERLAP) return null;
    const mid = (overlapStart + overlapEnd) / 2;
    const t = mid / lenA;
    if (t < 0.05 || t > 0.95) return null;
    return {
        x: a.x1 + mid * ux,
        y: a.y1 + mid * uy,
        t,
        u: 0.5
    };
}
function boundingBoxesOverlap(a, b) {
    const aMinX = Math.min(a.x1, a.x2);
    const aMaxX = Math.max(a.x1, a.x2);
    const aMinY = Math.min(a.y1, a.y2);
    const aMaxY = Math.max(a.y1, a.y2);
    const bMinX = Math.min(b.x1, b.x2);
    const bMaxX = Math.max(b.x1, b.x2);
    const bMinY = Math.min(b.y1, b.y2);
    const bMaxY = Math.max(b.y1, b.y2);
    return aMaxX >= bMinX && bMaxX >= aMinX && aMaxY >= bMinY && bMaxY >= aMinY;
}
export function findCrossings(ownSegments, otherEdgeId, otherSegments) {
    const crossings = [];
    for(let i = 0; i < ownSegments.length; i++){
        for(let j = 0; j < otherSegments.length; j++){
            if (!boundingBoxesOverlap(ownSegments[i], otherSegments[j])) continue;
            const hit = segmentIntersection(ownSegments[i], otherSegments[j]);
            if (hit) {
                crossings.push({
                    x: hit.x,
                    y: hit.y,
                    t: hit.t,
                    segmentIndex: i,
                    otherEdgeId
                });
            }
        }
    }
    crossings.sort((a, b)=>a.segmentIndex - b.segmentIndex || a.t - b.t);
    return crossings;
}
const DEFAULT_BRIDGE_RADIUS = 6;
function perpendicular(seg) {
    const dx = seg.x2 - seg.x1;
    const dy = seg.y2 - seg.y1;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 1e-10) return {
        nx: 0,
        ny: -1
    };
    let nx = -dy / len;
    let ny = dx / len;
    if (ny > 0) {
        nx = -nx;
        ny = -ny;
    }
    return {
        nx,
        ny
    };
}
function bridgeArcFragment(seg, crossing, radius) {
    const dx = seg.x2 - seg.x1;
    const dy = seg.y2 - seg.y1;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 1e-10) return "";
    const ux = dx / len;
    const uy = dy / len;
    const perp = perpendicular(seg);
    const beforeX = crossing.x - ux * radius;
    const beforeY = crossing.y - uy * radius;
    const afterX = crossing.x + ux * radius;
    const afterY = crossing.y + uy * radius;
    const cpX = crossing.x + perp.nx * radius * 1.2;
    const cpY = crossing.y + perp.ny * radius * 1.2;
    return `L ${beforeX} ${beforeY} Q ${cpX} ${cpY} ${afterX} ${afterY}`;
}
export function buildBridgePath(_originalPath, segments, crossings, radius = DEFAULT_BRIDGE_RADIUS) {
    if (crossings.length === 0 || segments.length === 0) return null;
    const bridgeEntries = computeAdaptiveBridges(crossings, segments, radius);
    if (bridgeEntries.length === 0) return null;
    const crossingsBySegment = new Map();
    for (const b of bridgeEntries){
        const list = crossingsBySegment.get(b.crossing.segmentIndex) ?? [];
        list.push(b);
        crossingsBySegment.set(b.crossing.segmentIndex, list);
    }
    let path = `M ${segments[0].x1} ${segments[0].y1}`;
    for(let i = 0; i < segments.length; i++){
        const seg = segments[i];
        const segBridges = crossingsBySegment.get(i);
        if (!segBridges || segBridges.length === 0) {
            path += ` L ${seg.x2} ${seg.y2}`;
        } else {
            for (const entry of segBridges){
                path += bridgeArcFragment(seg, entry.crossing, entry.radius);
            }
            path += ` L ${seg.x2} ${seg.y2}`;
        }
    }
    return path;
}
const MIN_BRIDGE_RADIUS = 3;
function computeAdaptiveBridges(crossings, segments, maxRadius) {
    if (crossings.length === 0) return [];
    let cumulativeLen = 0;
    const segStarts = [];
    const segLens = [];
    for (const seg of segments){
        segStarts.push(cumulativeLen);
        const len = Math.sqrt((seg.x2 - seg.x1) ** 2 + (seg.y2 - seg.y1) ** 2);
        segLens.push(len);
        cumulativeLen += len;
    }
    const infos = [];
    for (const c of crossings){
        const seg = segments[c.segmentIndex];
        const segLen = segLens[c.segmentIndex];
        if (segLen < 4) continue;
        const distFromSegStart = c.t * segLen;
        const distFromSegEnd = (1 - c.t) * segLen;
        infos.push({
            crossing: c,
            seg,
            segLen,
            absPos: segStarts[c.segmentIndex] + distFromSegStart,
            distFromSegStart,
            distFromSegEnd
        });
    }
    if (infos.length === 0) return [];
    const neighborDist = new Array(infos.length).fill(Infinity);
    for(let i = 0; i < infos.length; i++){
        if (i > 0) {
            const d = infos[i].absPos - infos[i - 1].absPos;
            neighborDist[i] = Math.min(neighborDist[i], d);
            neighborDist[i - 1] = Math.min(neighborDist[i - 1], d);
        }
        neighborDist[i] = Math.min(neighborDist[i], infos[i].distFromSegStart * 2, infos[i].distFromSegEnd * 2);
    }
    const entries = [];
    for(let i = 0; i < infos.length; i++){
        const maxFit = neighborDist[i] / 2;
        const r = Math.min(maxRadius, maxFit * 0.9);
        if (r < MIN_BRIDGE_RADIUS) continue;
        entries.push({
            crossing: infos[i].crossing,
            seg: infos[i].seg,
            radius: r
        });
    }
    return entries;
}

//# sourceMappingURL=edgeCrossingUtils.js.map